import React, { useCallback, useRef, useState } from "react";
import { CopyIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Post } from "api";
import { useConfirmDialog } from "components/confirmDialog";
import { handleCopyValue } from "utils";

interface ResetPartnerPasswordViewProps {
  partnerId: number;
}

const ResetPartnerPasswordView = (props: ResetPartnerPasswordViewProps) => {
  const { partnerId } = props;

  const toast = useToast();
  const { ConfirmDialog, openDialog } = useConfirmDialog({
    title: "Partner's password will be reset.",
    description:
      "You are about to reset the partner's password. This action cannot be reversed. The new password will appear next to the 'Reset Password' button.",
  });

  const newPartnerPassword = useRef("");
  const [newPartnerPasswordLoading, setNewPartnerPasswordLoading] =
    useState(false);

  const handleResetPartnerPassword = useCallback(async () => {
    if (!partnerId) return;

    setNewPartnerPasswordLoading(true);

    try {
      const partnerPasswordResetResponse = await Post(
        `/manage/partners/${partnerId}/reset-password`,
        {}
      );

      if (!partnerPasswordResetResponse.password) {
        toast({
          status: "error",
          title: "Reset Failed",
          description: "Partner password couldn't be reset. Please try again.",
        });
        return;
      }

      newPartnerPassword.current = partnerPasswordResetResponse.password;
      toast({
        status: "success",
        title: "Success",
        description: "Partner password reset successfully.",
      });
    } catch (error) {
      console.error(error);
    } finally {
      setNewPartnerPasswordLoading(false);
    }
  }, [partnerId, toast]);

  const onResetPasswordClick = useCallback(async () => {
    const result = await openDialog();

    if (result) {
      handleResetPartnerPassword();
    }
  }, [openDialog, handleResetPartnerPassword]);

  return (
    <Flex
      p={4}
      flexDir={"column"}
      mt={8}
      borderRadius={"16"}
      backgroundColor="white"
    >
      <Heading fontSize={"xl"}>Reset Password</Heading>
      <Flex mt={4} gap={4} alignItems="center">
        <Button
          bgColor={"brand.500"}
          color="white"
          _hover={{ backgroundColor: "brand.600" }}
          onClick={onResetPasswordClick}
        >
          Reset Password
        </Button>
        {newPartnerPasswordLoading ? (
          <Spinner />
        ) : newPartnerPassword.current ? (
          <Flex alignItems={"center"}>
            <Text color={"green.500"} fontWeight="medium">
              {newPartnerPassword.current.slice(0, 2)}••••••••••
              {newPartnerPassword.current.slice(
                newPartnerPassword.current.length - 2,
                newPartnerPassword.current.length
              )}
            </Text>
            <IconButton
              ml={2}
              aria-label="Copy API Key"
              icon={<CopyIcon />}
              onClick={() => {
                handleCopyValue(newPartnerPassword.current);
                toast({
                  title: "Copied to clipboard.",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                });
              }}
            />
          </Flex>
        ) : (
          <></>
        )}
      </Flex>
      {ConfirmDialog}
    </Flex>
  );
};

export default ResetPartnerPasswordView;
